/**
 * Cart Service - API Access for cart management
 * TypeScript implementation for Digital River API interactions
 * Based on GitLab marketplace_stg branch
 */

import { StorageUtils } from '../utils/storage.utils';
import { DOMUtils } from '../utils/dom.utils';

interface CartServiceOptions {
    locale: string;
    currency: string;
    environment?: CartEnvironment;
}

type CartEnvironment = 'prod' | 'stg' | 'dev' | 'qa' | 'preview';

interface EnvironmentEndpoints {
    muleEnv: string;
    buyEnv: string;
    memberEnv: string;
    partnerEnv: string;
    checkoutBaseUrl: string;
    cartEnv: string;
    cartEnvPhysical: string;
    productEnv: string;
}

const TRUSTED_PRODUCTION_HOSTS = new Set([
    'store.nvidia.com',
    'marketplace.nvidia.com',
    'www.nvidia.com'
]);

const ENVIRONMENT_ENDPOINTS: Record<CartEnvironment, EnvironmentEndpoints> = {
    prod: {
        muleEnv: 'https://api.store.nvidia.com/ucc-software/v2/cart',
        buyEnv: 'https://marketplace.nvidia.com/academy/buy/',
        memberEnv: 'https://api.store.nvidia.com/cart-management/v1/products',
        partnerEnv: 'https://api.store.nvidia.com/partner/v1',
        checkoutBaseUrl: 'https://marketplace.nvidia.com',
        cartEnv: 'https://api.store.nvidia.com/ucc-software/v2/cart/active',
        cartEnvPhysical: 'https://api.store.nvidia.com/cart-management/v2/cart/active',
        productEnv: 'https://api.nvidia.partners/products/v1'
    },
    stg: {
        muleEnv: 'https://stg-api.store.nvidia.com/ucc-software/v2/cart',
        buyEnv: 'https://stg-marketplace.nvidia.com/academy/buy/',
        memberEnv: 'https://stg-api.store.nvidia.com/cart-management/v1/products',
        partnerEnv: 'https://stg-api.store.nvidia.com/partner/v1',
        checkoutBaseUrl: 'https://stg-marketplace.nvidia.com',
        cartEnv: 'https://stg-api.store.nvidia.com/ucc-software/v2/cart/active',
        cartEnvPhysical: 'https://stg-api.store.nvidia.com/cart-management/v2/cart/active',
        productEnv: 'https://stg-api.nvidia.partners/products/v1'
    },
    dev: {
        muleEnv: 'https://dev-api.store.nvidia.com/ucc-software/v2/cart',
        buyEnv: 'https://stg-marketplace.nvidia.com/academy/buy/',
        memberEnv: 'https://dev-api.store.nvidia.com/cart-management/v1/products',
        partnerEnv: 'https://dev-api.store.nvidia.com/partner/v1',
        checkoutBaseUrl: 'https://stg-marketplace.nvidia.com',
        cartEnv: 'https://stg-api.store.nvidia.com/ucc-software/v2/cart/active',
        cartEnvPhysical: 'https://stg-api.store.nvidia.com/cart-management/v2/cart/active',
        productEnv: 'https://dev-api.nvidia.partners/products/v1'
    },
    qa: {
        muleEnv: 'https://qa-api.store.nvidia.com/ucc-software/v2/cart',
        buyEnv: 'https://stg-marketplace.nvidia.com/academy/buy/',
        memberEnv: 'https://qa-api.store.nvidia.com/cart-management/v1/products',
        partnerEnv: 'https://qa-api.store.nvidia.com/partner/v1',
        checkoutBaseUrl: 'https://stg-marketplace.nvidia.com',
        cartEnv: 'https://stg-api.store.nvidia.com/ucc-software/v2/cart/active',
        cartEnvPhysical: 'https://stg-api.store.nvidia.com/cart-management/v2/cart/active',
        productEnv: 'https://qa-api.nvidia.partners/products/v1'
    },
    preview: {
        muleEnv: 'https://api.store.nvidia.com/ucc-software/v2/cart',
        buyEnv: 'https://marketplace.nvidia.com/academy/buy/',
        memberEnv: 'https://api.store.nvidia.com/cart-management/v1/products',
        partnerEnv: 'https://api.store.nvidia.com/partner/v1',
        checkoutBaseUrl: 'https://marketplace.nvidia.com',
        cartEnv: 'https://api.store.nvidia.com/ucc-software/v2/cart/active',
        cartEnvPhysical: 'https://api.store.nvidia.com/cart-management/v2/cart/active',
        productEnv: 'https://api.nvidia.partners/products/v1'
    }
};

function normalizeEnvironment(value: string | null | undefined): CartEnvironment | null {
    if (!value) {
        return null;
    }
    const normalized = String(value).toLowerCase();
    if (normalized === 'stage') {
        return 'stg';
    }
    if (normalized === 'production' || normalized === 'prod') {
        return 'prod';
    }
    if (normalized === 'stg' || normalized === 'dev' || normalized === 'qa' || normalized === 'preview') {
        return normalized;
    }
    return null;
}

function inferEnvironmentFromHostname(hostname: string): CartEnvironment | null {
    const host = String(hostname || '').toLowerCase();
    if (!host) {
        return null;
    }

    if (host === 'localhost' || host === '127.0.0.1') {
        return 'stg';
    }

    if (host === 'preview.nvidia.com') {
        return 'preview';
    }

    if (TRUSTED_PRODUCTION_HOSTS.has(host)) {
        return 'prod';
    }

    // Environment inference is permitted only for NVIDIA-owned hostnames.
    if (host !== 'nvidia.com' && !host.endsWith('.nvidia.com')) {
        return null;
    }

    const firstSubdomain = host.split('.')[0];
    const environmentAliases: Record<string, CartEnvironment> = {
        dev: 'dev',
        wwwdev: 'dev',
        stg: 'stg',
        stage: 'stg',
        wwwstg: 'stg',
        wwwstage: 'stg',
        qa: 'qa',
        wwwqa: 'qa',
        preview: 'preview'
    };
    const environmentLabel = firstSubdomain.split('-')[0];

    if (environmentAliases[environmentLabel]) {
        return environmentAliases[environmentLabel];
    }

    return null;
}

interface ProductItem {
    id?: string | number;
    productId?: string | number;
    sku?: string;
    quantity: number;
}

// Supports numeric marketplace IDs and IDs such as `course:14113686_14816925`,
// while rejecting URL delimiters and other characters that could alter a query string.
const SAFE_PRODUCT_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9:_-]{0,127}$/;

function encodeCheckoutLineItem(item: ProductItem): string {
    const rawProductId = item.id ?? item.productId;
    const productId = rawProductId == null ? '' : String(rawProductId);
    const quantity = Number(item.quantity);

    if (!SAFE_PRODUCT_ID_PATTERN.test(productId)) {
        throw new Error('[nvCart] Invalid product ID in checkout request.');
    }
    if (!Number.isSafeInteger(quantity) || quantity < 1) {
        throw new Error('[nvCart] Invalid product quantity in checkout request.');
    }

    return `${encodeURIComponent(productId)}_${encodeURIComponent(String(quantity))}`;
}

interface CheckoutOptions {
    items: any[];
    challenge?: any;
    isDigital: boolean;
    category: string;
}

interface AddLineItemsOptions {
    products: ProductItem[];
    challenge?: any;
    isDigital: boolean;
    category: string;
}

interface FEInventoryOptions {
    products: string;
}

const getCurrencyFromLocale = (locale: string): string => {
    switch (locale) {
        case 'en_gb':
            return 'GBP';
        case 'en_us':
            return 'USD';
        default:
            return 'USD';
    }
};

class CartServiceImpl {
    private host: string;
    private muleEnv: string;
    private buyenv: string;
    private memberEnv: string;
    private partnerEnv: string;
    private checkoutEnv: string;
    private cartEnv: string;
    private cartEnvPhysical: string;
    private productEnv: string;
    private readonly environmentOverride: CartEnvironment | null;

    constructor(
        private opts: CartServiceOptions,
        private nvLocale: string,
        private apiCurrency: string,
        private PromoLocale: string,
        private isHeadless: boolean,
        private actionTypes: any,
        private store: any
    ) {
        if (!opts) {
            throw new Error("Marketplace: You must provide options for locale and currency");
        }
        if (!opts.locale) {
            throw new Error("Marketplace: You must set an option for locale");
        }
        if (!opts.currency) {
            opts.currency = getCurrencyFromLocale(opts.locale);
        }

        this.host = document.location.hostname;
        this.environmentOverride = normalizeEnvironment(opts.environment);
        if (opts.environment != null && !this.environmentOverride) {
            throw new Error(`[nvCart] Unsupported environment "${opts.environment}".`);
        }
        this.muleEnv = '';
        this.buyenv = '';
        this.memberEnv = '';
        this.partnerEnv = '';
        this.checkoutEnv = '';
        this.cartEnv = '';
        this.cartEnvPhysical = '';
        this.productEnv = '';

        this.initializeEnvironments();
    }

    private initializeEnvironments(): void {
        const inferredEnvironment = inferEnvironmentFromHostname(this.host);
        const environment = this.environmentOverride || inferredEnvironment;
        if (!environment) {
            throw new Error(
                `[nvCart] Untrusted host "${this.host}". ` +
                'Provide an explicit environment or run the SDK on an approved NVIDIA host.'
            );
        }

        const endpoints = ENVIRONMENT_ENDPOINTS[environment];
        const countryQuery = this.nvLocale.split('-')[1].toUpperCase();
        const checkoutLocale = this.nvLocale === 'de-at' ? 'de-de' : this.nvLocale;

        this.muleEnv = endpoints.muleEnv;
        this.buyenv = endpoints.buyEnv;
        this.memberEnv = endpoints.memberEnv;
        this.partnerEnv = endpoints.partnerEnv;
        this.checkoutEnv = `${endpoints.checkoutBaseUrl}/${checkoutLocale}/cart`;
        this.cartEnv = endpoints.cartEnv;
        this.cartEnvPhysical = endpoints.cartEnvPhysical;
        this.productEnv = endpoints.productEnv;

        if (environment !== 'prod') {
            const buyUrl = new URL(this.buyenv);
            buyUrl.searchParams.set('country', countryQuery);
            this.buyenv = buyUrl.toString();
        }
    }

    getProducts = (prodIDs: any): Promise<any> => {
        // Add loading class before fetch
        // if (!DOMUtils.hasClass(document.body, "loading")) {
        //     DOMUtils.addClass(document.body, "loading");
        // }

        return fetch(this.memberEnv + '/membership', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(prodIDs)
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            });
        // .finally(() => {
        //     if (DOMUtils.hasClass(document.body, "loading")) {
        //         DOMUtils.removeClass(document.body, "loading");
        //     }
        // });
    };

    getFEInventory = (opts: FEInventoryOptions): Promise<any> => {
        const products = opts.products.includes('_') ? opts.products.split('_')[0] : opts.products;
        const url = `${this.partnerEnv}/feinventory?status=1&skus=${products}&locale=${this.nvLocale}`;

        return fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include'  // Include cookies for authentication
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            });
    };

    getCartData = async (skuId: string): Promise<any> => {
        const loc = this.nvLocale.split('-')[1];
        const url = `${this.cartEnv}?enable=mini_cart`;

        // if (!DOMUtils.hasClass(document.body, "loading")) {
        //     DOMUtils.addClass(document.body, "loading");
        // }

        try {
            const response = await fetch(url, {
                method: "GET",
                credentials: 'include'
            });
            const data = await response.json();

            // if (DOMUtils.hasClass(document.body, "loading")) {
            //     DOMUtils.removeClass(document.body, "loading");
            // }

            return data;
        } catch (error) {
            // if (DOMUtils.hasClass(document.body, "loading")) {
            //     DOMUtils.removeClass(document.body, "loading");
            // }
            return null;
        }
    };

    addLineItems = (opts: AddLineItemsOptions): any => {
        const items: any[] = [];
        let products: any, headerBody: any, reqBody: any, callURL: string;
        sessionStorage.setItem('checkoutClicked', 'true');
        // Create comma-separated query string payload: productId_sku_quantity,productId_sku_quantity,...
        const pidQueryString = opts.products.map((item: any) => {
            const productId = item.id || item.productId;
            const sku = item.sku || productId; // Fallback to productId if sku is not available
            const quantity = item.quantity;
            return `${productId}_${quantity}`;
        }).join(',');
        sessionStorage.setItem('category', opts.category);
        sessionStorage.setItem('directBuy', JSON.stringify(opts));
        if (opts.isDigital) {
            const querySeparator = this.buyenv.includes('?') ? '&' : '?';
            callURL = `${this.buyenv}${querySeparator}pid=${pidQueryString}`;
            // Set checkout cookie to expire in 1 hour (1/24 days)
            StorageUtils.setCookie('checkout', JSON.stringify(true), 1 / 24);
            window.location.href = callURL;
        } else {
            callURL = this.cartEnvPhysical;
            if (opts.category === 'nvidia') {
                // callURL = this.isHeadless 
                //     ? (opts.isDigital ? this.muleEnv + "/checkout" : this.cartEnv) 
                //     : this.muleEnv + "/checkout";

                opts.products.forEach((item) => {
                    items.push({
                        'productId': item.id ? item.id : item.productId,
                        'quantity': item.quantity
                    });
                });

                products = {
                    "locale": this.PromoLocale,
                    "currency": this.apiCurrency,
                    "isDigital": opts.isDigital,
                    "products": items
                };
                reqBody = { "cart": products };
                headerBody = { "Content-Type": "application/json" };
            } else {
                console.log('newPayload==>', this.PromoLocale, this.apiCurrency);

                opts.products.forEach((item) => {
                    items.push({
                        'productId': item.id ? item.id : item.productId,
                        'quantity': item.quantity
                    });
                });

                products = {
                    "locale": this.PromoLocale,
                    "currency": this.apiCurrency,
                    "isDigital": opts.isDigital,
                    "products": items
                };
                reqBody = { "cart": products };
                headerBody = { "Content-Type": "application/json", partner: true };
            }
            if (items.length === 1) {
                const directPids = sessionStorage.getItem('directPids');
                console.log('items[0][productId]===>', items[0]['productId']);
                if (items[0]['productId'] === Number(directPids)) {
                    headerBody = { "Content-Type": "application/json", partner: true };
                }
            }
            return fetch(callURL, {
                method: "POST",
                headers: headerBody,
                body: JSON.stringify(reqBody),
                credentials: 'include'
            }).then(async (response) => {
                const data = await response.json();

                if (!response.ok) {
                    throw {
                        status: response.status,
                        statusText: response.statusText,
                        responseJSON: data,
                        responseText: data
                    };
                }

                console.log('Response==>', data);
                sessionStorage.setItem('checkoutClicked', 'false');
                sessionStorage.removeItem('directBuy');

                if (this.isHeadless && !opts.isDigital) {
                    window.location.href = this.checkoutEnv;
                }

                return data;
            }).catch((error: any) => {
                console.log("Cart Checkout Error==>", error);

                const errorElement = document.querySelector('.errorCart__updates') as HTMLElement;
                if (errorElement) {
                    errorElement.style.display = 'block';
                }

                document.body.classList.remove("loading");

                if (error.statusText === "Bad Request") {
                    if (!error.responseText.success) {
                        this.store.dispatch({
                            type: this.actionTypes.SET_CART,
                            cart: []
                        });
                    }
                }

                if (error.status >= 500) {
                    sessionStorage.setItem('checkoutClicked', 'false');
                }

                if (error.status === 409) {
                    sessionStorage.setItem('checkoutClicked', 'false');
                    error.responseJSON.forEach((confItem: any) => {
                        const key = Object.keys(confItem)[0];
                        if (confItem[key].errors && confItem[key].errors.error[0].code === "inventory-unavailable-error") {
                            opts.products.forEach((item) => {
                                if (item.id == key) {
                                    this.store.dispatch({
                                        type: this.actionTypes.UPDATE_INVENTORY,
                                        products: item
                                    });
                                }
                            });
                        }
                    });
                }
                throw error;
            });
        }
    };

    checkout = (opts: CheckoutOptions): void => {
        if (!DOMUtils.hasClass(document.body, "loading")) {
            DOMUtils.addClass(document.body, "loading");
        }

        const items = opts.items;
        const storedSession = {};
        const category = opts.category;

        if (opts.category !== 'nvidia') {
            this.addLineItems({
                products: items,
                challenge: opts.challenge,
                isDigital: opts.isDigital,
                category: category
            });
        } else {
            this.addLineItems({
                products: items,
                challenge: opts.challenge,
                isDigital: opts.isDigital,
                category: category
            });
        }
    };
}

export const CartService = {
    getFEInventory: null as any,
    addLineItems: null as any,
    getCartData: null as any,
    getProducts: null as any,
    checkout: null as any,

    initialize(opts: CartServiceOptions, nvLocale: string, apiCurrency: string,
        PromoLocale: string, isHeadless: boolean, actionTypes: any, store: any) {
        const instance = new CartServiceImpl(opts, nvLocale, apiCurrency, PromoLocale,
            isHeadless, actionTypes, store);
        this.getFEInventory = instance.getFEInventory.bind(instance);
        this.addLineItems = instance.addLineItems.bind(instance);
        this.getCartData = instance.getCartData.bind(instance);
        this.getProducts = instance.getProducts.bind(instance);
        this.checkout = instance.checkout.bind(instance);
        return this;
    }
};

// Legacy export for backward compatibility
export const ApiAccess = CartService.initialize;

// Default export
export default CartService;
